import { USDC } from "../../../../../models/UsdcValue";
import { CURRENCY } from "../../../../../utils/Constants";
import ball from "../../../../../assets/images/mango_ball.png";
import { useRive } from "@rive-app/react-canvas";
import shuffle from "../../../../../assets/animations/shuffle.riv";
import { useSpring, animated, easings } from "react-spring";
import { formatDate, mumboJumbo } from "../../../../../utils/Utils";
import { useInView } from "react-intersection-observer";

export interface JackpotProps {
  /**
   * @description Value of the jackpot.
   */
  value: USDC | undefined;
  /**
   * @description Date of the draw.
   */
  date: Date | undefined;
  /**
   * @description Whether the draw is open (accepting ticket purchases) or not.
   */
  isOpen: boolean;
}

/**
 * @description Jackpot component. Contains the value of the jackpot and the date of the draw.
 */
export default function Jackpot(props: JackpotProps) {
  const { RiveComponent } = useRive({
    src: shuffle,
    stateMachines: "state_machine",
    autoplay: true,
  });

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const jackpotAnimation = useSpring({
    from: { number: 0 },
    to: { number: inView ? Number(props.value?.value) : 0 },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inView,
  });

  return (
    <div ref={ref} data-aos="fade-down" data-aos-delay="200">
      <div className="flex flex-row">
        <img
          src={ball}
          className="select-none ~mr-7/8 ~h-9/11 ~w-9/11 animate-bounce"
          alt="mango-ball"
        />
        {/**
         * Jackpot Value
         */}
        <div
          key={props.value?.value}
          className="~mt-2/3 ~mb-2/3 animate-in spin-in-3 hover:scale-105 duration-250"
        >
          <p className="~mb-0.5/1 font-barlowc font-bold ~text-base/lg bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
            JACKPOT
          </p>
          <div className="flex flex-row">
            <animated.div className="font-archb font-bold ~text-xxxl/xxxxxl ~leading-9/12 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
              {jackpotAnimation.number.to((n) =>
                mumboJumbo(n, props.value?.pretty.length ?? 0),
              )}
            </animated.div>
            <p className="font-archb font-bold ~text-xxxl/xxxxxl ~leading-9/12 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
              &nbsp;{CURRENCY}
            </p>
          </div>
          <div className="flex flex-row items-center h-[32px] space-x-2 ~mt-1/2">
            {!props.isOpen && (
              <div className="w-[32px] h-[32px]">
                <RiveComponent />
              </div>
            )}
            <p
              className={`font-poppins font-bold ~text-xs/sm text-gray transition-all duration-250`}
            >
              {props.isOpen
                ? `Play until ${formatDate(props.date) ?? "..."}`
                : "Draw starting soon..."}
            </p>
          </div>
        </div>
        <img
          src={ball}
          className="select-none ~ml-7/8 ~h-9/11 ~w-9/11 self-end animate-bounce delay-250"
          alt="mango-ball"
        />
      </div>
    </div>
  );
}
